import {useState, forwardRef, ReactNode, useEffect, useMemo} from 'react'
import dayjs from 'dayjs'
import {isSameDate} from './utils/isSameDate'
import CalendarBase from './CalendarBase'
import type {CommonProps} from '../@types/common'
import type {CalendarSharedProps} from './CalendarBase'
import type {Modifiers} from './tables/components/types'
import useControllableState from "@/components/ui/hooks/useControllableState";

export interface RangeCalendarProps
    extends CommonProps,
        Omit<
            CalendarSharedProps,
            | 'value'
            | 'isDateInRange'
            | 'isDateFirstInRange'
            | 'isDateLastInRange'
        > {
    value: [Date | null, Date | null]
    onChange: (value: [Date | null, Date | null]) => void
    singleDate?: boolean
    prevIcon?: ReactNode
    prevLabel?: ReactNode
    nextIcon?: ReactNode
    nextLabel?: ReactNode
}

const RangeCalendar = forwardRef<HTMLDivElement, RangeCalendarProps>(
    (props, ref) => {
        const {
            value,
            onChange,
            singleDate = true,
            dateViewCount = 1,
            paginateBy,
            prevIcon,
            prevLabel,
            nextIcon,
            nextLabel,
            dayStyle,
            dayClassName,
            disabled,
            ...rest
        } = props

        const [hoveredDay, setHoveredDay] = useState<Date | null>(null)
        const [pickedDate, setPickedDate] = useState<Date | null>(null)


        const selectionRange = useMemo(() => [pickedDate, hoveredDay].sort((a, b) => a?.getTime() - b?.getTime()), [hoveredDay, pickedDate]);


        const setRangeDate = (date: Date) => {
            if (pickedDate instanceof Date) {
                if (isSameDate(date, pickedDate) && !singleDate) {
                    setPickedDate(null)
                    setHoveredDay(null)
                    return null
                }

                const result: [Date, Date] = [date, pickedDate]
                result.sort((a, b) => a.getTime() - b.getTime())
                onChange(result)
                setPickedDate(null)
                return null
            }

            if (value[0] && isSameDate(date, value[0]) && !singleDate) {
                setPickedDate(null)
                setHoveredDay(null)
                onChange([null, null])
                return null
            }

            onChange([date, null])
            setPickedDate(date)
            return null
        }

        const shouldHighlightDate = (date: Date, modifiers: Modifiers) => {
            if (pickedDate instanceof Date && hoveredDay instanceof Date) {
                const result = [hoveredDay, pickedDate]
                result.sort((a, b) => a.getTime() - b.getTime())
                return (
                    !modifiers.selected &&
                    dayjs(date).calendar('jalali').subtract(1, 'day').isBefore(result[1]) &&
                    dayjs(date).calendar('jalali').add(1, 'day').isAfter(result[0])
                )
            }

            return false
        }

        const isPickedDateFirstInRange = (date: Date, modifiers: Modifiers) => {
            if (pickedDate instanceof Date && hoveredDay instanceof Date) {
                const result = [hoveredDay, pickedDate]
                result.sort((a, b) => a.getTime() - b.getTime())
                return modifiers.selected && dayjs(date).calendar('jalali').isBefore(result[1])
            }

            return false
        }

        const isPickedDateLastInRange = (date: Date, modifiers: Modifiers) => {
            if (pickedDate instanceof Date && hoveredDay instanceof Date) {
                const result = [hoveredDay, pickedDate]
                result.sort((a, b) => a.getTime() - b.getTime())
                return modifiers.selected && dayjs(date).calendar('jalali').isAfter(result[0])
            }

            return false
        }

        return (
            <CalendarBase
                ref={ref}
                dayStyle={dayStyle}
                dayClassName={dayClassName}
                value={pickedDate}
                range={value as [Date, Date]}
                selectionRange={selectionRange}
                dateViewCount={dateViewCount}
                paginateBy={paginateBy || dateViewCount}
                hideOutOfMonthDates={(dateViewCount as number) > 1}
                onDayMouseEnter={(date) => setHoveredDay(date)}
                onChange={(date) => setRangeDate(date as Date)}
                prevIcon={prevIcon}
                prevLabel={prevLabel}
                nextIcon={nextIcon}
                nextLabel={nextLabel}
                {...rest}
            />
        )
    }
)

RangeCalendar.displayName = 'RangeCalendar'

export default RangeCalendar
